/**
 * Colour primitives that PARSE a colour string in JS.
 *
 * PRODUCT-154 turned every `src/styles/color-theme.ts` export into a
 * `var(--wh-*)` string. That is exactly right in CSS (a stylesheet or a `style`
 * prop resolves it, and it tracks the active theme for free) and exactly wrong
 * the moment the string reaches JS colour maths, which has to parse it.
 *
 * The sanctioned answer is still `src/styles/raw.ts` — a call site that does
 * colour maths should read REAL hex, not a token (see the guard test in
 * `src/styles/__tests__/raw-escape-hatch.guard.test.ts`). `resolveCssVar` is the
 * second line of defence for the values we do NOT control: free-text colours
 * persisted by tenants, imports, and older builds — one of which (a
 * waste fraction defaulted to `var(--wh-gray-800)`) took the whole page down
 * through the error boundary.
 */

/** How many `var(--a, var(--b, …))` fallback hops to follow before giving up. */
const MAX_VAR_HOPS = 4

const VAR_RE = /^var\(\s*(--[^,)\s]+)\s*(?:,([\s\S]*))?\)$/

/**
 * Read a `var(--x)` / `var(--x, fallback)` token off the document root.
 *
 * Returns '' when the input is not a `var()` token at all, when there is no
 * document (SSR, jsdom before the theme stylesheet is applied) and no fallback,
 * or when the custom property is not defined — so every caller can decide its
 * own degraded value rather than throwing out of render.
 */
export const resolveCssVar = (value: string): string => {
  let current = value

  for (let hop = 0; hop < MAX_VAR_HOPS; hop += 1) {
    const match = VAR_RE.exec(current.trim())
    if (!match) return hop === 0 ? '' : current.trim()

    const [, name, fallback] = match
    const fromDocument =
      typeof window !== 'undefined' && typeof document !== 'undefined'
        ? window
            .getComputedStyle(document.documentElement)
            .getPropertyValue(name)
            .trim()
        : ''
    if (fromDocument) return fromDocument
    if (!fallback) return ''
    // The fallback may itself be a `var()` — keep unwrapping.
    current = fallback.trim()
  }

  return ''
}

/**
 * Whatever entered JS, as something a colour parser has a chance with.
 *
 * A `var()` token is resolved against the document; anything else (a hex, an
 * `rgb()`, a CSS colour name, or junk) is passed through untouched for the
 * caller's own parser to accept or reject.
 */
export const resolveColorString = (value?: string | null): string => {
  const candidate = value?.trim()
  if (!candidate) return ''
  return candidate.startsWith('var(') ? resolveCssVar(candidate) : candidate
}

export const hexToRgb = (hex: string) => {
  // A `var(--wh-*)` token reaches here whenever a design token is handed to
  // colour maths instead of `raw.ts`; without this it just fails the regex and
  // the caller silently treats the colour as black.
  const resolved = resolveColorString(hex)
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(resolved)
  return (
    result && {
      red: parseInt(result[1], 16),
      green: parseInt(result[2], 16),
      blue: parseInt(result[3], 16),
    }
  )
}

export const pickTextColor = (color: string, dark = '#000', light = '#fff') => {
  const { red = 0, green = 0, blue = 0 } = hexToRgb(color) || {}
  const brightness = red * 0.299 + green * 0.587 + blue * 0.114
  return brightness > 186 ? dark : light
}
